import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*

// Fáze 2: detail hráče (medailonek) — kariérní souhrn + ligová výkonnost po sezónách.
// Data z team-profile (jméno) + team-player-history (sezóny). Foto/bio přijdou ve fázi 3.

// Výsledek hráče na jednom turnaji (veřejné, z sgbowling sg_tournament*).
private data class PlayerTournamentResult(
    val name: String,
    val date: String,
    val gameType: String,
    val placement: Int?,
    val games: Int,
    val average: Double,
    val best: Int,
)

@Composable
fun PlayerDetailScreen(playerId: String, onBack: () -> Unit) {
    var name by remember { mutableStateOf<String?>(null) }
    var sgProfile by remember { mutableStateOf<SgProfileDto?>(null) }
    var seasons by remember { mutableStateOf<List<PlayerSeasonStatsDto>>(emptyList()) }
    var tournaments by remember { mutableStateOf<List<PlayerTournamentResult>>(emptyList()) }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }

    LaunchedEffect(playerId) {
        try {
            sgProfile = runCatching { Api.fetchSgProfiles().find { it.blPlayerId == playerId } }.getOrNull()
            val profile = Api.fetchTeamProfile()
            val rosterEntry = profile.roster.find { it.playerId == playerId }
            val hist = Api.fetchTeamPlayerHistory().players.find { it.playerId == playerId }
            name = sgProfile?.displayName?.takeIf { it.isNotBlank() }
                ?: rosterEntry?.fullName ?: rosterEntry?.name ?: hist?.fullName ?: hist?.name
            seasons = hist?.seasons ?: emptyList()

            // Turnajové výsledky (veřejné): turnaje, kde má hráč skóre nebo umístění.
            val tours = Api.fetchPublicTournaments().associateBy { it.id }
            val scoresByTour = Api.fetchPlayerTournamentScores(playerId).groupBy { it.tournamentId ?: "" }
            val placeByTour = Api.fetchPlayerPlacements(playerId).associate { (it.tournamentId ?: "") to it.placement }
            tournaments = tours.values
                .filter { scoresByTour.containsKey(it.id) || placeByTour.containsKey(it.id) }
                .map { t ->
                    val sc = scoresByTour[t.id] ?: emptyList()
                    val avg = if (sc.isNotEmpty()) (sc.map { it.score }.average() * 10).toInt() / 10.0 else 0.0
                    PlayerTournamentResult(t.name, t.date, t.gameType, placeByTour[t.id], sc.size, avg, sc.maxOfOrNull { it.score } ?: 0)
                }
                .sortedByDescending { it.date }
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    Button(attrs = { classes("back-button"); onClick { onBack() } }) { Text("← Zpět na hráče") }

    when {
        loading -> Div(attrs = { classes("card") }) { P { Text("Načítám…") } }
        error != null -> Div(attrs = { classes("card") }) { P { Text("Chyba: $error") } }
        else -> {
            Div(attrs = { classes("card", "player-header") }) {
                val photo = sgProfile?.photoUrl
                if (!photo.isNullOrBlank()) {
                    Img(src = photo, alt = name ?: "Hráč", attrs = { classes("avatar-lg", "player-photo-lg") })
                } else {
                    Div(attrs = { classes("avatar-placeholder", "avatar-lg") }) { Text((name ?: "?").take(1)) }
                }
                Div {
                    H2 { Text(name ?: "Hráč") }
                    sgProfile?.nickname?.takeIf { it.isNotBlank() }?.let { Div(attrs = { classes("medallion-nick") }) { Text("„$it“") } }
                    val isCaptain = sgProfile?.isCaptain ?: (playerId == CAPTAIN_PLAYER_ID)
                    if (playerId == ADMIN_PLAYER_ID) Span(attrs = { classes("badge", "badge-admin") }) { Text("admin") }
                    if (isCaptain) Span(attrs = { classes("badge") }) { Text("kapitán") }
                }
            }
            sgProfile?.bioHtml?.takeIf { it.isNotBlank() }?.let {
                Div(attrs = { classes("card") }) { RichContent(it) }
            }
            if (seasons.isNotEmpty()) {
                CareerSummary(seasons)
                SeasonBreakdown(seasons)
            }
            if (tournaments.isNotEmpty()) {
                TournamentResults(tournaments)
            }
            if (seasons.isEmpty() && tournaments.isEmpty()) {
                Div(attrs = { classes("card") }) { P(attrs = { classes("muted") }) { Text("Zatím žádná data.") } }
            }
        }
    }
}

@Composable
private fun TournamentResults(results: List<PlayerTournamentResult>) {
    Div(attrs = { classes("card") }) {
        H3 { Text("Turnaje") }
        Div(attrs = { classes("training-list") }) {
            results.forEach { r ->
                Div(attrs = { classes("training-item") }) {
                    Div(attrs = { classes("training-item-top") }) {
                        Div(attrs = { classes("training-head") }) {
                            r.placement?.let { Span(attrs = { classes("placement-chip") }) { Text("${it}. místo") } }
                            Span(attrs = { classes("training-date") }) { Text(" ${r.name}") }
                            Span(attrs = { classes("muted") }) { Text(" · ${r.date} · ${bowlingTypeLabel(r.gameType)}") }
                        }
                        Div(attrs = { classes("tour-stats") }) {
                            Span(attrs = { classes("tour-avg") }) { Text("Ø ${r.average}") }
                            Span(attrs = { classes("muted") }) { Text(" ${r.games} her · max ${r.best}") }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CareerSummary(seasons: List<PlayerSeasonStatsDto>) {
    val totalGames = seasons.sumOf { it.games }
    val weightedAvg = if (totalGames > 0)
        (seasons.sumOf { it.average * it.games } / totalGames * 10).toInt() / 10.0 else 0.0
    val bestGame = seasons.maxOfOrNull { it.maxScore } ?: 0
    val worstGame = seasons.minOfOrNull { it.minScore } ?: 0

    Div(attrs = { classes("card") }) {
        H3 { Text("Kariérní souhrn (liga)") }
        Div(attrs = { classes("career-stats") }) {
            StatBox("$weightedAvg", "průměr")
            StatBox("$totalGames", "her")
            StatBox("$bestGame", "nejlepší hra")
            StatBox("$worstGame", "nejhorší hra")
            StatBox("${seasons.size}", if (seasons.size == 1) "sezóna" else "sezón")
        }
    }
}

@Composable
private fun StatBox(value: String, label: String) {
    Div(attrs = { classes("stat-pill") }) {
        Span(attrs = { classes("stat-value") }) { Text(value) }
        Span(attrs = { classes("stat-label") }) { Text(label) }
    }
}

@Composable
private fun SeasonBreakdown(seasons: List<PlayerSeasonStatsDto>) {
    val maxAvg = seasons.maxOfOrNull { it.average } ?: 1.0
    Div(attrs = { classes("card") }) {
        H3 { Text("Výkonnost po sezónách") }
        Div(attrs = { classes("season-list") }) {
            seasons.forEach { s ->
                Div(attrs = { classes("season-row") }) {
                    Div(attrs = { classes("season-name") }) { Text(s.seasonName) }
                    Div(attrs = { classes("season-bar-wrap") }) {
                        val pct = if (maxAvg > 0) (s.average / maxAvg * 100).toInt() else 0
                        Div(attrs = {
                            classes("season-bar")
                            attr("style", "width:${pct}%")
                        }) { Text("Ø ${s.average}") }
                    }
                    Div(attrs = { classes("season-detail") }) { Text("${s.games} her · ${s.minScore}–${s.maxScore}") }
                }
            }
        }
    }
}
